/**
 * OpsResHome (B2) -- the Resilience Lead's own landing page.
 *
 * Lives in `twin/` rather than `shared/`: its heaviest sources are the
 * IBS portfolio, the review scan and the drill register, and the
 * frontend enforces the same boundary the backend does -- shared/ must
 * not reach into a feature module.
 *
 * She landed on the generic home, which named her nowhere and told her
 * nothing about what she was being asked to do. Everything here is
 * already derived somewhere in the product; the work is putting HER
 * pieces on one screen in the order she needs them:
 *
 *   what is waiting on her signature -> what is breaching right now ->
 *   what is falling due -> where the bank sits on the ladder.
 *
 * No new backend. If a panel's source is unavailable it says so rather
 * than rendering an encouraging zero.
 */
import { SinceLastLooked } from "twin/whatsnew/SinceLastLooked";
import { itemTitle } from "shared/work/itemTitle";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { roleLabel } from "shared/vocab/workflowState";
import { useQuery } from "@tanstack/react-query";

import { api } from "shared/api/client";
import { useMe } from "shared/api/useMe";
import { Card } from "shared/components/Card";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { ReadinessCard } from "shared/readiness/ReadinessCard";
import { RecommendationsCard } from "shared/recommendations/RecommendationsCard";
import { useJourneySpineStatus } from "shared/journey/useJourney";
import { useMyWork } from "shared/work/useMyWork";
import { routeForStep, stepsForOwner } from "shared/journey/stepsForOwner";
import { useIBSReviewScan } from "twin/api/ibs_admission_hooks";
import { useDrills } from "twin/api/drill_hooks";

interface PortfolioSummary {
  total: number;
  admitted: number;
  mvb: number;
  red: number;
  breached_now: number;
}

function usePortfolioSummary() {
  return useQuery({
    queryKey: ["twin", "ibs-portfolio"],
    queryFn: () => api.get<PortfolioSummary>("/v1/twin/ibs-portfolio"),
    refetchInterval: 60_000,
  });
}

function Figure({
  value,
  label,
  tone,
  to,
  testid,
}: {
  value: number | string;
  label: string;
  tone?: string;
  to: string;
  testid: string;
}) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex flex-1 flex-col rounded border border-slate-200 bg-white px-3 py-2 hover:border-brand-brass"
    >
      <span
        className={`font-display text-2xl font-semibold ${tone ?? "text-slate-900"}`}
      >
        {value}
      </span>
      <span className="mt-0.5 text-xs text-slate-500">{label}</span>
    </Link>
  );
}

export function OpsResHome() {
  const { t } = useTranslation();
  const me = useMe();
  const work = useMyWork();
  const portfolio = usePortfolioSummary();
  const reviews = useIBSReviewScan();
  const drills = useDrills();
  const journey = useJourneySpineStatus();

  const mine = (work.data?.items ?? []).filter((w) => w.ownership !== "queue");
  const next = stepsForOwner(journey.data?.steps, "opsres");
  const dueDrills = (drills.data ?? []).filter(
    (d) => d.status === "planned" || d.status === "in_progress",
  );

  return (
    <article data-testid="opsres-home" className="space-y-6">
      <header>
        <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
          {t("opsResHome.rubric")}
        </p>
        {/* ROUND 13 H -- an h2, not an h1. This desk renders in the
            body of `/`, and the night-sky hero above it already
            carries the page's h1, so two h1 elements landed on one
            document. The hero heading is the page; this is the
            section of it that is his. */}
        <h2 className="mt-1 font-display text-2xl text-slate-900">
          <MarsadIcon
            name="twin"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("opsResHome.heading", {
            name: me.data?.display_name ?? me.data?.username ?? "",
          })}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-slate-700">
          {t("opsResHome.positioning")}
        </p>
      </header>

      {/* Round 20 U7 -- what arrived since her last look. */}
      <SinceLastLooked surface="home" />

      {/* 1. What is waiting on HER. Not a queue she shares. */}
      <Card micro={t("opsResHome.waiting.heading")} icon="dual-signoff">
        {mine.length === 0 ? (
          <p data-testid="opsres-waiting-empty" className="mt-2 text-sm text-slate-500">
            {t("opsResHome.waiting.empty")}
          </p>
        ) : (
          <ul className="mt-2 space-y-1.5" data-testid="opsres-waiting">
            {mine.slice(0, 5).map((w) => (
              <li key={w.id}>
                <Link
                  to="/my-work"
                  className="flex items-baseline gap-2 text-sm text-slate-800 hover:text-brand-brassDark"
                >
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10.5px] font-medium uppercase text-amber-900">
                    {roleLabel(w.my_role)}
                  </span>
                  {/* Round 18 I18 -- the server's title by its key, as My Work reads it. */}
                  <span className="truncate">{itemTitle(t, w)}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* 2. What is wrong right now. */}
      <section className="space-y-2">
        <h2 className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
          {t("opsResHome.now.heading")}
        </h2>
        {portfolio.isError ? (
          <p data-testid="opsres-now-error" className="text-sm text-slate-500">
            {t("opsResHome.now.unavailable")}
          </p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="opsres-now">
            <Figure
              testid="opsres-breaching"
              value={portfolio.data?.breached_now ?? "--"}
              label={t("opsResHome.now.breaching")}
              tone="text-rag-red"
              to="/twin/ibs-portfolio"
            />
            <Figure
              testid="opsres-red"
              value={portfolio.data?.red ?? "--"}
              label={t("opsResHome.now.red")}
              tone="text-rag-red"
              to="/twin/cascade"
            />
            <Figure
              testid="opsres-reviews"
              value={reviews.data?.overdue_count ?? "--"}
              label={t("opsResHome.now.reviewsOverdue")}
              to="/twin/ibs-admission"
            />
            <Figure
              testid="opsres-drills"
              value={dueDrills.length}
              label={t("opsResHome.now.drills")}
              to="/twin/drills"
            />
          </div>
        )}
      </section>

      {/* 3. Where the bank sits, filtered to what she owns. */}
      <Card micro={t("opsResHome.next.heading")} icon="journey">
        {next.length === 0 ? (
          <p data-testid="opsres-next-empty" className="mt-2 text-sm text-slate-500">
            {t("opsResHome.next.empty")}
          </p>
        ) : (
          <ol className="mt-2 space-y-1.5" data-testid="opsres-next">
            {next.slice(0, 4).map((s, i) => {
              const route = routeForStep(s.key);
              const label = t(`modules.journey.step.${s.key}.title`);
              return (
                <li key={s.key} className="flex items-baseline gap-2 text-sm">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10.5px] font-medium uppercase ${
                      s.status === "blocked"
                        ? "bg-rose-100 text-rose-900"
                        : i === 0
                          ? "bg-brand-brass/15 text-brand-brassDark"
                          : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {t(`modules.journey.status.${s.status}`)}
                  </span>
                  {route ? (
                    <Link
                      to={route}
                      data-testid={`opsres-next-${s.key}`}
                      className="text-slate-800 hover:text-brand-brassDark"
                    >
                      {label}
                    </Link>
                  ) : (
                    <span>{label}</span>
                  )}
                  {s.key === "incidents" && (
                    <Link
                      to="/twin/findings"
                      data-testid="opsres-next-open-incidents"
                      className="text-xs text-brand-brassDark hover:underline"
                    >
                      {t("opsResHome.next.seeOpenIncidents")}
                    </Link>
                  )}
                  {s.total !== null && (
                    <span className="text-xs text-slate-400">
                      {s.count}/{s.total}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </Card>

      {/* E3 -- the recommendations moved to Framework, so the person
          whose job is deciding what to do about the estate had to
          leave her own desk to find out. It is the same card; it
          belongs on both. */}
      <RecommendationsCard />

      <ReadinessCard />
    </article>
  );
}
